import { Group, Text } from "@mantine/core";
import type { ColumnDef } from "@tanstack/react-table";
import { Money, LocalMoney } from "@/components/Money";
import { Mono, ExternalMono } from "@/components/Mono";
import { Timestamp } from "@/components/Timestamp";
import { RelatedLink } from "@/components/RelatedLink";
import { AdminActionButton } from "@/components/AdminActionButton";
import { JsonExpander } from "@/components/JsonExpander";
import { LedgerRefundLink } from "@/components/LedgerRefundAssociations";
import { humanize } from "@/lib/format";
import { txUrl } from "@/lib/explorer";
import { COLLECTIONS } from "@/firestore/collections";
import type {
  HoldDoc,
  LedgerEntryDoc,
  OwnerKeyBackupDoc,
  OwnerOperationDoc,
  PaymentDoc,
  SessionKeyDoc,
  SmartAccountOperationDoc,
  WalletMigrationDoc,
  WalletTransactionDoc,
  WithDocId,
} from "@/firestore/types";
import type { EntityConfig } from "../types";
import {
  markPaymentCaptured,
  markPaymentDeclined,
  markPaymentExpired,
  markPaymentReversed,
  rescheduleSettlement,
  revokeSessionKey,
  requestSessionKeyInstall,
  forceReprovisionAutonomousKey,
  AUTONOMOUS_KEY_TYPES,
  type AutonomousKeyType,
  expireHold,
  failHold,
  releaseHold,
  resolveSmartAccountOperation,
  forceAbandonSmartAccountOperation,
  supportRecoverOwnerKeyBackup,
} from "@/api/actions";
import { monoCol, moneyCol, relatedCol, stateCol, textCol, timeCol } from "../shared";
import { col } from "../columns";
import { KmsStatusBadge, KmsStatusDetail } from "@/components/KmsStatus";

type P<T> = WithDocId<T>;

// ─────────────────────────────── Payment ───────────────────────────────

export const paymentConfig: EntityConfig<PaymentDoc> = {
  key: "payment",
  collection: COLLECTIONS.payments,
  title: "Payment",
  pluralTitle: "Payments",
  ownerField: "owner",
  defaultOrder: [["approvedAt", "desc"]],
  columns: [
    monoCol<P<PaymentDoc>>("id", "ID", (r) => r.id),
    stateCol<P<PaymentDoc>>((r) => r.state),
    moneyCol<P<PaymentDoc>>("amount", "Amount", (r) => r.amountMicroUsdc),
    textCol<P<PaymentDoc>>("merchant", "Merchant", (r) => r.merchantName),
    textCol<P<PaymentDoc>>("mcc", "MCC", (r) => r.merchantCategoryCode),
    relatedCol<P<PaymentDoc>>("card", "Card", "card", (r) => r.card),
    relatedCol<P<PaymentDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<PaymentDoc>>("approvedAt", "Approved", (r) => r.approvedAt),
    timeCol<P<PaymentDoc>>("expiresAt", "Expires", (r) => r.expiresAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Amount (USDC)", value: <Money micro={d.amountMicroUsdc} fw={600} /> },
    {
      label: "Local amount",
      value: <LocalMoney minor={d.localAmountMinor} currency={d.localCurrency} />,
    },
    { label: "Merchant", value: d.merchantName },
    { label: "MCC", value: d.merchantCategoryCode ?? "—" },
    { label: "Card", value: <RelatedLink entity="card" id={d.card} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.account} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Approved", value: <Timestamp value={d.approvedAt} absolute /> },
    { label: "Expires", value: <Timestamp value={d.expiresAt} absolute /> },
    { label: "Captured", value: <Timestamp value={d.capturedAt} absolute /> },
    { label: "Declined", value: <Timestamp value={d.declinedAt} absolute /> },
    {
      label: "Decline reason code",
      value: d.declinedReasonCode ? humanize(d.declinedReasonCode) : "—",
    },
    { label: "Decline reason", value: d.declinedReason ?? "—", full: true },
    { label: "Rain authorization id", value: <Mono value={d.providerAuthorizationId ?? null} /> },
  ],
  related: (d) => [
    { title: "Confirmed merchant refunds", entity: "cardRefund", collection: COLLECTIONS.cardRefunds, fkField: "originalPayment", value: d.id, orderBy: [["createdAt", "asc"]] },
    { title: "Converted refund record", entity: "cardRefund", collection: COLLECTIONS.cardRefunds, fkField: "legacyPayment", value: d.id, orderBy: [["createdAt", "asc"]] },
    {
      title: "Settlement obligation",
      entity: "settlementObligation",
      collection: COLLECTIONS.settlementObligations,
      fkField: "sourcePayment",
      value: d.id,
    },
    {
      title: "Reserve top-up legs",
      entity: "reserveTopUpLeg",
      collection: COLLECTIONS.reserveTopUpLegs,
      fkField: "sourcePayment",
      value: d.id,
      orderBy: [["legIndex", "asc"]],
    },
    {
      title: "Holds",
      entity: "hold",
      collection: COLLECTIONS.holds,
      fkField: "sourcePayment",
      value: d.id,
    },
    {
      title: "Smart-account operations",
      entity: "smartAccountOperation",
      collection: COLLECTIONS.smartAccountOperations,
      fkField: "sourcePayment",
      value: d.id,
    },
  ],
  actions: (d) => (
    <Group gap="xs">
      <AdminActionButton
        action={markPaymentCaptured}
        params={{
          id: d.id,
          capturedAt: d.approvedAt,
          finalAmountMicroUsdc: d.amountMicroUsdc,
          updatedAt: d.updatedAt,
        }}
      />
      <AdminActionButton
        action={markPaymentExpired}
        params={{ id: d.id, updatedAt: d.updatedAt }}
      />
      <AdminActionButton
        action={markPaymentDeclined}
        params={{
          id: d.id,
          declinedReasonCode: "providerInitiatedDecline",
          reason: "",
          updatedAt: d.updatedAt,
        }}
      />
      <AdminActionButton
        action={markPaymentReversed}
        params={{ id: d.id, updatedAt: d.updatedAt }}
      />
      <AdminActionButton action={rescheduleSettlement} params={{ paymentId: d.id }} />
    </Group>
  ),
};

// ─────────────────────────────── Session Key ───────────────────────────────

export const sessionKeyConfig: EntityConfig<SessionKeyDoc> = {
  key: "sessionKey",
  collection: COLLECTIONS.sessionKeys,
  title: "Session Key",
  pluralTitle: "Session Keys",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<SessionKeyDoc>>("id", "ID", (r) => r.id),
    stateCol<P<SessionKeyDoc>>((r) => r.state),
    textCol<P<SessionKeyDoc>>("type", "Type", (r) => r.type, { humanize: true }),
    textCol<P<SessionKeyDoc>>("target", "Target", (r) => r.targetSmartAccount, { humanize: true }),
    monoCol<P<SessionKeyDoc>>("publicKey", "Key", (r) => r.publicKey),
    // KMS-material teardown status — fetched on demand from the admin endpoint (batched), not
    // a Firestore field, so KMS internals never touch the owner-readable mobile sessionKeys doc.
    col<P<SessionKeyDoc>>({
      id: "kms",
      header: "KMS",
      cell: (r) => <KmsStatusBadge keyId={r.id} />,
    }),
    relatedCol<P<SessionKeyDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<SessionKeyDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => {
    const stuckPending = d.state === "pending";
    const wrongTarget = d.type?.includes("cctp") && d.targetSmartAccount === "main";
    return [
      { label: "Type", value: humanize(d.type) },
      { label: "Target smart account", value: humanize(d.targetSmartAccount) },
      { label: "Public key", value: <Mono value={d.publicKey} /> },
      { label: "Recipient (user-transfer)", value: <Mono value={d.recipientAddress} /> },
      { label: "Max amount (user-transfer)", value: <Money micro={d.maxAmountMicroUsdc} /> },
      { label: "Valid after", value: <Timestamp value={d.validAfter} absolute /> },
      { label: "Valid until", value: <Timestamp value={d.validUntil} absolute /> },
      { label: "Enabled on-chain", value: <Timestamp value={d.enabledOnChainAt} absolute /> },
      { label: "Revoked", value: <Timestamp value={d.revokedAt} absolute /> },
      { label: "KMS material", value: <KmsStatusDetail keyId={d.id} />, full: true },
      { label: "Wallet", value: <RelatedLink entity="wallet" id={d.account} /> },
      { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
      ...(stuckPending
        ? [
            {
              label: "⚠ Flag",
              value: <Text c="orange">Stuck pending — awaiting device signature (install).</Text>,
              full: true,
            },
          ]
        : []),
      ...(wrongTarget
        ? [
            {
              label: "⚠ Flag",
              value: <Text c="red">cctp-settlement key targeting Main (expected Reserve).</Text>,
              full: true,
            },
          ]
        : []),
    ];
  },
  related: (d) => [
    {
      title: "Smart-account operations",
      entity: "smartAccountOperation",
      collection: COLLECTIONS.smartAccountOperations,
      fkField: "sessionKey",
      value: d.id,
    },
  ],
  actions: (d) => {
    const isAutonomous = (AUTONOMOUS_KEY_TYPES as readonly string[]).includes(d.type);
    return (
      <Group gap="xs">
        <AdminActionButton action={revokeSessionKey} params={{ id: d.id }} />
        <AdminActionButton action={requestSessionKeyInstall} params={{ id: d.id }} />
        {isAutonomous && d.state === "revoked" ? (
          <AdminActionButton
            action={forceReprovisionAutonomousKey}
            params={{ walletId: d.account, type: d.type as AutonomousKeyType }}
          />
        ) : null}
      </Group>
    );
  },
};

// ─────────────────────────────── Owner Operation ───────────────────────────────

export const ownerOperationConfig: EntityConfig<OwnerOperationDoc> = {
  key: "ownerOperation",
  collection: COLLECTIONS.ownerOperations,
  title: "Owner Operation",
  pluralTitle: "Owner Operations",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<OwnerOperationDoc>>("id", "ID", (r) => r.id),
    stateCol<P<OwnerOperationDoc>>((r) => r.state),
    textCol<P<OwnerOperationDoc>>("type", "Type", (r) => r.type, { humanize: true }),
    relatedCol<P<OwnerOperationDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<OwnerOperationDoc>>("expiresAt", "Expires", (r) => r.expiresAt),
    timeCol<P<OwnerOperationDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Type", value: humanize(d.type) },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.account} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Hash to sign", value: <Mono value={d.hashToSign} /> },
    { label: "Error", value: d.errorMessage ?? "—", full: true },
    { label: "Expires", value: <Timestamp value={d.expiresAt} absolute /> },
    { label: "Payload", value: <JsonExpander value={d.payload} />, full: true },
  ],
};

// ─────────────────────────────── Hold ───────────────────────────────

export const holdConfig: EntityConfig<HoldDoc> = {
  key: "hold",
  collection: COLLECTIONS.holds,
  title: "Hold",
  pluralTitle: "Holds",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<HoldDoc>>("id", "ID", (r) => r.id),
    stateCol<P<HoldDoc>>((r) => r.state),
    textCol<P<HoldDoc>>("kind", "Kind", (r) => r.kind, { humanize: true }),
    textCol<P<HoldDoc>>("role", "Role", (r) => r.accountRole, { humanize: true }),
    moneyCol<P<HoldDoc>>("amount", "Amount", (r) => r.amountMicroUsdc),
    relatedCol<P<HoldDoc>>("wallet", "Wallet", "wallet", (r) => r.wallet),
    timeCol<P<HoldDoc>>("expiresAt", "Expires", (r) => r.expiresAt),
    timeCol<P<HoldDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Kind", value: humanize(d.kind) },
    { label: "Account role", value: humanize(d.accountRole) },
    { label: "Amount", value: <Money micro={d.amountMicroUsdc} fw={600} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.wallet} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Source payment", value: <RelatedLink entity="payment" id={d.sourcePayment} /> },
    {
      label: "Source withdrawal",
      value: <RelatedLink entity="withdrawal" id={d.sourceWithdrawal} />,
    },
    { label: "Source deposit", value: <RelatedLink entity="deposit" id={d.sourceDeposit} /> },
    {
      label: "Source SAO",
      value: <RelatedLink entity="smartAccountOperation" id={d.sourceSmartAccountOperation} />,
    },
    { label: "Expires", value: <Timestamp value={d.expiresAt} absolute /> },
    { label: "Released", value: <Timestamp value={d.releasedAt} absolute /> },
    { label: "Failure reason", value: d.failureReason ?? "—", full: true },
  ],
  actions: (d) => (
    <Group gap="xs">
      {d.state === "active" ? (
        <AdminActionButton action={expireHold} params={{ id: d.id, updatedAt: d.updatedAt }} />
      ) : null}
      {d.state === "pending" || d.state === "active" ? (
        <>
          <AdminActionButton action={failHold} params={{ id: d.id, updatedAt: d.updatedAt }} />
          <AdminActionButton action={releaseHold} params={{ id: d.id, updatedAt: d.updatedAt }} />
        </>
      ) : null}
    </Group>
  ),
};

// ─────────────────────────────── Wallet Migration ───────────────────────────────

export const walletMigrationConfig: EntityConfig<WalletMigrationDoc> = {
  key: "walletMigration",
  collection: COLLECTIONS.walletMigrations,
  title: "Wallet Migration",
  pluralTitle: "Migrations",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<WalletMigrationDoc>>("id", "ID", (r) => r.id),
    stateCol<P<WalletMigrationDoc>>((r) => r.state),
    relatedCol<P<WalletMigrationDoc>>("old", "From wallet", "wallet", (r) => r.oldWallet),
    relatedCol<P<WalletMigrationDoc>>("new", "To wallet", "wallet", (r) => r.newWallet),
    textCol<P<WalletMigrationDoc>>("rotation", "Owner rotation", (r) =>
      r.ownerRotation ? "yes" : "no",
    ),
    relatedCol<P<WalletMigrationDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<WalletMigrationDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "From wallet", value: <RelatedLink entity="wallet" id={d.oldWallet} /> },
    { label: "To wallet", value: <RelatedLink entity="wallet" id={d.newWallet} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "From generation", value: d.fromGeneration },
    { label: "To generation", value: d.toGeneration },
    { label: "Owner rotation", value: d.ownerRotation ? "yes" : "no" },
    { label: "New owner address", value: <Mono value={d.newOwnerAddress} /> },
    { label: "Completed", value: <Timestamp value={d.completedAt} absolute /> },
    { label: "Scheduled cleanup", value: <Timestamp value={d.scheduledCleanupAt} absolute /> },
    { label: "Cleaned up", value: <Timestamp value={d.cleanedUpAt} absolute /> },
    { label: "Failure reason", value: d.failureReason ?? "—", full: true },
  ],
};

// ─────────────────────────────── Owner-Key Backup ───────────────────────────────
//
// ⚠ METADATA ONLY. See the security contract on `OwnerKeyBackupDoc` in `firestore/types.ts`.
//
// Everything on this surface answers one question: does this user have a recoverable backup of
// their owner key, is it current, and when was it written. The ciphertext is not here, is not in
// Firestore at all, and has no admin read endpoint. There is no decryption path and no
// support-assisted recovery — `supportRecoverOwnerKeyBackup` is rendered as a permanent tombstone
// so that stays visible instead of looking like a missing feature.
//
// No `redactRaw` on this config, deliberately. Nothing in the document needs hiding, and adding a
// mask would imply otherwise — which is how someone ends up projecting the envelope and "masking"
// it client-side, where masking is not an access control (see `CredentialsBlock` in ramps.tsx).

/** Disabled explanation only: backup records expose no administrator mutation. */
function ownerKeyBackupActions(d: P<OwnerKeyBackupDoc>) {
  return (
    <AdminActionButton action={supportRecoverOwnerKeyBackup} params={{ keyScope: d.keyScope }} />
  );
}

export const ownerKeyBackupConfig: EntityConfig<OwnerKeyBackupDoc> = {
  key: "ownerKeyBackup",
  collection: COLLECTIONS.ownerKeyBackups,
  title: "Owner-Key Backup",
  pluralTitle: "Owner-Key Backups",
  ownerField: "owner",
  defaultOrder: [["backedUpAt", "desc"]],
  columns: [
    monoCol<P<OwnerKeyBackupDoc>>("keyScope", "Key scope", (r) => r.keyScope),
    stateCol<P<OwnerKeyBackupDoc>>((r) => r.state),
    textCol<P<OwnerKeyBackupDoc>>("secretType", "Secret", (r) => r.secretType, { humanize: true }),
    textCol<P<OwnerKeyBackupDoc>>("algorithm", "Algorithm", (r) => r.algorithm),
    textCol<P<OwnerKeyBackupDoc>>("version", "Version", (r) =>
      typeof r.backupVersion === "number" ? String(r.backupVersion) : null,
    ),
    monoCol<P<OwnerKeyBackupDoc>>("ownerAddress", "Owner address", (r) => r.ownerAddress),
    relatedCol<P<OwnerKeyBackupDoc>>("account", "Wallet", "wallet", (r) => r.account),
    relatedCol<P<OwnerKeyBackupDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<OwnerKeyBackupDoc>>("backedUpAt", "Backed up", (r) => r.backedUpAt),
  ],
  subtitle: (d) => <Mono value={d.keyScope} />,
  detailFields: (d) => [
    {
      // The scope is the root wallet id, so it stays stable across version-upgrade migrations and
      // changes only on an owner rotation. That is what makes "is this backup current?" answerable.
      label: "Key scope (root wallet)",
      value: <RelatedLink entity="wallet" id={d.keyScope} />,
    },
    { label: "Wallet at write time", value: <RelatedLink entity="wallet" id={d.account} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Owner address", value: <Mono value={d.ownerAddress} /> },
    { label: "Secret type", value: humanize(d.secretType) },
    { label: "Algorithm", value: <Mono value={d.algorithm} copy={false} short={false} /> },
    { label: "Backup version", value: d.backupVersion ?? "—" },
    { label: "Backed up", value: <Timestamp value={d.backedUpAt} absolute /> },
    { label: "First enrolled", value: <Timestamp value={d.createdAt} absolute /> },
    { label: "Superseded", value: <Timestamp value={d.supersededAt} absolute /> },
    {
      label: "Superseded by wallet",
      value: <RelatedLink entity="wallet" id={d.supersededByWallet} />,
    },
    {
      label: "Superseded reason",
      value: d.supersededReason ? humanize(d.supersededReason) : "—",
    },
    { label: "Deleted", value: <Timestamp value={d.deletedAt} absolute /> },
    {
      label: "What this is",
      full: true,
      value: (
        <Text size="sm" c="dimmed">
          Metadata only. The recovery secret itself is encrypted on the user&apos;s device under a
          password they chose and we never receive; we hold ciphertext we cannot decrypt. Nothing on
          this screen, and no endpoint behind it, can reveal a recovery phrase.
        </Text>
      ),
    },
  ],
  actions: (d) => ownerKeyBackupActions(d),
};

// ─────────────────────────────── Smart-Account Operation ───────────────────────────────

export const smartAccountOperationConfig: EntityConfig<SmartAccountOperationDoc> = {
  key: "smartAccountOperation",
  collection: COLLECTIONS.smartAccountOperations,
  title: "Smart-Account Op",
  pluralTitle: "Smart-Account Ops",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<SmartAccountOperationDoc>>("id", "ID", (r) => r.id),
    stateCol<P<SmartAccountOperationDoc>>((r) => r.state),
    textCol<P<SmartAccountOperationDoc>>("type", "Type", (r) => r.type, { humanize: true }),
    relatedCol<P<SmartAccountOperationDoc>>("account", "Wallet", "wallet", (r) => r.account),
    monoCol<P<SmartAccountOperationDoc>>("tx", "Tx", (r) => r.transactionHash),
    timeCol<P<SmartAccountOperationDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Type", value: humanize(d.type) },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.account} /> },
    { label: "Session key", value: <RelatedLink entity="sessionKey" id={d.sessionKey} /> },
    { label: "Source payment", value: <RelatedLink entity="payment" id={d.sourcePayment} /> },
    { label: "UserOp hash", value: <Mono value={d.userOpHash} /> },
    {
      label: "Tx hash",
      value: <ExternalMono value={d.transactionHash} href={txUrl(d.transactionHash)} />,
    },
    { label: "Submitted", value: <Timestamp value={d.submittedAt} absolute /> },
    { label: "Included", value: <Timestamp value={d.includedAt} absolute /> },
    { label: "Error", value: d.errorMessage ?? "—", full: true },
    { label: "Call data", value: <Mono value={d.callData} short copy />, full: true },
  ],
  actions: (d) =>
    d.state === "submissionUnknown" ? (
      <Group gap="xs">
        <AdminActionButton
          action={resolveSmartAccountOperation}
          params={{ id: d.id, updatedAt: d.updatedAt }}
        />
        <AdminActionButton
          action={forceAbandonSmartAccountOperation}
          params={{ id: d.id, updatedAt: d.updatedAt }}
        />
      </Group>
    ) : null,
};

// ─────────────────────────────── Ledger Entry (list-only) ───────────────────────────────

export const ledgerEntryColumns: ColumnDef<P<LedgerEntryDoc>, unknown>[] = [
  stateCol<P<LedgerEntryDoc>>((r) => r.direction, "Direction"),
  textCol<P<LedgerEntryDoc>>("category", "Category", (r) => r.category, { humanize: true }),
  moneyCol<P<LedgerEntryDoc>>("amount", "Amount", (r) => r.amountMicroUsdc),
  monoCol<P<LedgerEntryDoc>>("tx", "Tx", (r) => r.txHash),
  timeCol<P<LedgerEntryDoc>>("blockTimestamp", "Block time", (r) => r.blockTimestamp),
  { id: "cardRefund", header: "Card refund", cell: ({ row }) => <LedgerRefundLink ledgerId={row.original.id} /> },
];

export const walletTransactionColumns: ColumnDef<P<WalletTransactionDoc>, unknown>[] = [
  textCol<P<WalletTransactionDoc>>("type", "Type", (r) => r.type, { humanize: true }),
  moneyCol<P<WalletTransactionDoc>>("amount", "Amount", (r) => r.amountMicroUsdc),
  textCol<P<WalletTransactionDoc>>("role", "Role", (r) => r.smartAccountRole, { humanize: true }),
  relatedCol<P<WalletTransactionDoc>>("payment", "Payment", "payment", (r) => r.payment),
  timeCol<P<WalletTransactionDoc>>("createdAt", "Created", (r) => r.createdAt),
  { id: "cardRefund", header: "Card refund", cell: ({ row }) => <LedgerRefundLink ledgerId={row.original.ledgerEntry} /> },
];
